// ============================================================
// HANDOUTS (v8.363.9) - what the GM shows the table.
//
// A handout is a record with an AUDIENCE ([] = the GM's draft, '*' the
// table, '@players', or uids) and a kind: an image or a PDF beside it as
// a file, a markdown note, or a link. The limits here are the client's;
// the 50 MiB per file is also the rule on both servers (src/backend/schema.ts
// declares it for the generated rules, storage.rules by hand - tests hold all
// three to this constant). The 1 GiB a session is the client's alone: a rule
// cannot sum.
// ============================================================
import { readOrigin } from '../assets/provenance';
import type { Origin } from '../assets/types';

export type HandoutKind = 'image' | 'pdf' | 'note' | 'link';

export const MAX_FILE_BYTES = 50 * 1024 * 1024;
export const MAX_SESSION_BYTES = 1024 * 1024 * 1024;
export const MAX_EDGE_PX = 2048;
export const THUMB_EDGE_PX = 256;
export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
export const PDF_TYPE = 'application/pdf';

/**
 * A file the GM's add did not take: its name, the short reason a batch
 * lists it with, and the sentence a single add shows on its own.
 */
export interface Refused {
  name: string;
  reason: string;
  sentence: string;
}

export interface Handout {
  id: string;
  kind: HandoutKind;
  title: string;
  folder: string;
  tags: string[];
  audience: string[];
  notes: string;
  url: string;
  /** The files port's reference, '' when none (a note, a link, a draft not yet uploaded). */
  file: string;
  thumb: string;
  size: number;
  width: number;
  height: number;
  pages: number;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  revealedAt: number;
  /** When the GM put it up on every screen in its audience (v9.8.0, engine/handouts/present); 0 = not presented. */
  presentedAt: number;
  /** The library asset and version an image or a PDF was copied from (v11.82.0); null for one posted at the table. */
  origin: Origin | null;
}

/** The document as it travels: `tags` stringified. */
export interface HandoutDoc {
  kind: string;
  title: string;
  folder: string;
  tags: string;
  audience: string[];
  notes: string;
  url: string;
  file?: string;
  thumb?: string;
  size: number;
  width: number;
  height: number;
  pages: number;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  revealedAt: number;
  presentedAt?: number;
  /** `origin` as JSON; '' or absent for none. */
  origin?: string;
}

const KINDS: HandoutKind[] = ['image', 'pdf', 'note', 'link'];

export function toDoc(h: Handout): Omit<HandoutDoc, 'file' | 'thumb'> {
  return {
    kind: h.kind,
    title: h.title,
    folder: h.folder,
    tags: JSON.stringify(h.tags),
    audience: h.audience,
    notes: h.notes,
    url: h.url,
    size: h.size,
    width: h.width,
    height: h.height,
    pages: h.pages,
    createdBy: h.createdBy,
    createdAt: h.createdAt,
    updatedAt: h.updatedAt,
    revealedAt: h.revealedAt,
    presentedAt: h.presentedAt,
    origin: h.origin ? JSON.stringify(h.origin) : '',
  };
}

export function fromDoc(id: string, d: Partial<HandoutDoc> | null | undefined): Handout {
  return {
    id,
    kind: KINDS.includes(d?.kind as HandoutKind) ? (d!.kind as HandoutKind) : 'note',
    title: str(d?.title),
    folder: str(d?.folder),
    tags: parseTags(d?.tags),
    audience: stringsIn(d?.audience),
    notes: str(d?.notes),
    url: str(d?.url),
    file: str(d?.file),
    thumb: str(d?.thumb),
    size: num(d?.size),
    width: num(d?.width),
    height: num(d?.height),
    pages: num(d?.pages),
    createdBy: str(d?.createdBy),
    createdAt: num(d?.createdAt),
    updatedAt: num(d?.updatedAt),
    revealedAt: num(d?.revealedAt),
    presentedAt: num(d?.presentedAt),
    origin: originOf(d?.origin),
  };
}

/** A stored origin, or none when it is absent or will not read. */
function originOf(stored: unknown): Origin | null {
  try {
    return typeof stored === 'string' && stored ? readOrigin(JSON.parse(stored)) : null;
  } catch {
    return null;
  }
}

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** The strings in a stored array; anything else there is dropped. */
function stringsIn(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((a): a is string => typeof a === 'string') : [];
}

/** The stored tag list (a JSON string), or none when it is absent or corrupt. */
function parseTags(stored: unknown): string[] {
  try {
    return stringsIn(typeof stored === 'string' && stored ? JSON.parse(stored) : []);
  } catch {
    return [];
  }
}

/** A draft, before anything is uploaded. */
export function emptyHandout(id: string, kind: HandoutKind, by: string, now: number): Handout {
  return {
    id,
    kind,
    title: '',
    folder: '',
    tags: [],
    audience: [],
    notes: '',
    url: '',
    file: '',
    thumb: '',
    size: 0,
    width: 0,
    height: 0,
    pages: 0,
    createdBy: by,
    createdAt: now,
    updatedAt: now,
    revealedAt: 0,
    presentedAt: 0,
    origin: null,
  };
}
